import { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Light / dark theme (redesign.md, Fase 2). The user picks Automático / Claro /
// Oscuro in Configuración; "system" follows the phone (prefers-color-scheme).
// The resolved theme lives on <html data-theme="light|dark"> — index.css keys
// every color off that attribute. public/index.html runs the same resolution
// inline before React loads, so the first paint is already in the right theme.
//
// The preference is stored in localStorage (instant on next open, works before
// login) and on the profile (theme_preference), so it follows the account to
// other devices — App syncs the profile value in via setPreference.

export const THEME_STORAGE_KEY = 'mp_theme';
const PREFERENCES = ['system', 'light', 'dark'];
// Browser/PWA status bar — matches --bg of each theme.
const THEME_COLOR = { light: '#F5F3FF', dark: '#150A2E' };

const ThemeContext = createContext({ preference: 'system', theme: 'dark', setPreference: () => {} });

function readStoredPreference() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.includes(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

const systemTheme = () =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}

// `forcedTheme` pins a theme regardless of preference (the internal /admin
// panel stays dark — it isn't part of the user-facing redesign).
export function ThemeProvider({ forcedTheme, children }) {
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [system, setSystem] = useState(systemTheme);
  const theme = forcedTheme || (preference === 'system' ? system : preference);

  // Follow the phone switching light/dark while the app is open.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const onChange = () => setSystem(mq.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Applied during render, not in an effect: children that read theme
  // colors from CSS (cssVar, used by charts / the tour) render before a
  // parent's effects run, and would otherwise read the previous theme.
  // Idempotent attribute writes, so re-renders are harmless.
  applyTheme(theme);

  const setPreference = useCallback((value) => {
    if (!PREFERENCES.includes(value)) return;
    setPreferenceState(value);
    try { localStorage.setItem(THEME_STORAGE_KEY, value); } catch { /* private mode — session only */ }
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// Current value of a theme CSS variable, for places that need a literal color
// string instead of a class (Recharts props, react-joyride styles). Call it
// during render of a component that uses useTheme(), so it re-reads on switch.
// '--brand' → 'rgb(124 58 237)'; '--brand-rgb' → '124 58 237'.
export function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
